import React, { useEffect, useRef, useState } from "react";
import { analyticsAPI } from "../../../services/analyticsAPI";
import { api } from "../../../services/apiClient";
import ExistingGlobalAnalyticsOverview from "./ExistingGlobalAnalyticsOverview";

const sections = ["Overview", "Inspector Performance", "QA/QC Performance", "Work Contribution", "User Activity & Engagement", "Weekly Reports"];
const period = () => {
  const d = new Date();
  const end = d.toISOString().slice(0, 10);
  d.setDate(d.getDate() - 6);
  return { start: d.toISOString().slice(0, 10), end, timezone: "UTC", project_id: "" };
};

const METRIC_LABELS = {
  name: "Employee", project_name: "Project Name", project: "Project Name",
  inspector_name: "Inspector", reviewer_name: "QA/QC Reviewer",
  created: "Inspections Created", submitted: "Unique Inspections Submitted",
  submission_events: "Submission Events", resubmissions: "Resubmissions",
  approved: "Unique Inspections Approved", rejected: "Unique Inspections Rejected",
  rejection_events: "Rejection Decisions", reopened: "Reopened Inspections",
  pending_review_now: "Pending Review (Now)", first_pass_approvals: "First-Pass Approvals",
  first_pass_approval_rate: "First-Pass Approval Rate",
  completed_inspection_checklists: "Completed Inspection Checklists",
  findings_on_period_created_inspections: "Findings on Inspections Created in Period (Current Count)",
  preparation: "Preparation Time", rework: "Rework Time", review_turnaround: "Review Turnaround",
  reviews: "Review Decisions", repeat_rejections: "Repeat Rejections",
  report_date: "Date (UTC)", user_name: "Employee", event_type: "Activity",
  event_count: "Events", visible_seconds: "Visible Time", active_seconds: "Interaction-Indicated Time",
  idle_seconds: "Visible Idle Time", created_at: "Created At (UTC)",
};
const humanLabel = (key) => METRIC_LABELS[key] || key.replaceAll("_", " ").replace(/\b\w/g, ch => ch.toUpperCase());
const duration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (total >= 3600) return `${(total / 3600).toFixed(2)} h`;
  if (total >= 60) return `${Math.floor(total / 60)} min ${total % 60} sec`;
  return `${total} sec`;
};
const metricValue = (value, key = "") => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(v => metricValue(v)).join(", ");
  if (typeof value === "object") {
    if ("average_hours" in value || "median_hours" in value) {
      if (value.average_hours == null && value.median_hours == null)
        return "No recorded measurements";
      return `Average ${value.average_hours ?? "—"} h · Median ${value.median_hours ?? "—"} h · ${value.observations ?? 0} measured`;
    }
    return Object.entries(value).map(([k, v]) => `${humanLabel(k)}: ${metricValue(v, k)}`).join(" · ");
  }
  if (key.endsWith("_seconds")) return duration(value);
  if (key === "first_pass_approval_rate") return `${value}%`;
  if (typeof value === "number") return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return String(value).replaceAll("_", " ");
};
const ProjectSelect = ({ value, onChange, projects }) => (
  <label className="flex flex-col gap-1">Project
    <select aria-label="Project" className="border border-slate-300 rounded-md bg-white p-2 min-w-44" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">All projects</option>
      {projects.map(project => <option key={project.id} value={project.id}>{project.name || `Project #${project.id}`}{project.project_number ? ` (${project.project_number})` : ""}</option>)}
    </select>
  </label>
);
const UserSelect = ({ value, onChange, users, label = "Employee" }) => (
  <label className="flex flex-col gap-1">{label}
    <select aria-label={label} className="border border-slate-300 rounded-md bg-white p-2 min-w-48 max-w-72" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">All users</option>
      {users.map(user => <option key={user.id} value={user.id}>{`${user.first_name || ""} ${user.last_name || ""}`.trim() || `User #${user.id}`}{user.email ? ` — ${user.email}` : ""}</option>)}
    </select>
  </label>
);

const AnalyticsTable = ({ rows, projects = [], users = [] }) => {
  if (!rows?.length) return <p className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">No recorded data for this scope and period.</p>;
  const projectNames = new Map(projects.map(project => [Number(project.id), project.name]));
  const userNames = new Map(users.map(user => [Number(user.id), `${user.first_name || ""} ${user.last_name || ""}`.trim()]));
  const prepared = rows.map(item => {
    const row = {};
    if (item.name || item.user_name || item.user_id != null)
      row.employee = item.name || item.user_name || userNames.get(Number(item.user_id)) || `User #${item.user_id}`;
    if (item.project_id !== undefined)
      row.project_name = item.project_name || projectNames.get(Number(item.project_id)) || (item.project_id == null ? "Global / Not project-scoped" : `Project #${item.project_id}`);
    for (const [key, value] of Object.entries(item)) {
      if (["user_id", "user_name", "name", "project_id", "project_name"].includes(key)) continue;
      row[key] = value;
    }
    return row;
  });
  const keys = [...new Set(prepared.flatMap(row => Object.keys(row)))];
  return <div className="rounded-lg border border-slate-200 overflow-x-auto max-h-[620px] overflow-y-auto">
    <table className="min-w-full text-sm border-collapse">
      <thead className="sticky top-0 z-10 bg-slate-100"><tr>{keys.map(key =>
        <th key={key} scope="col" className="border-b border-slate-200 p-3 text-left font-semibold text-slate-700 whitespace-normal min-w-28">{key === "employee" ? "Employee" : humanLabel(key)}</th>)}</tr></thead>
      <tbody>{prepared.map((row, index) => <tr key={index} className="border-b border-slate-100 odd:bg-white even:bg-slate-50 hover:bg-orange-50/50">
        {keys.map(key => <td key={key} className="px-3 py-2.5 align-top text-slate-800 whitespace-normal min-w-28">{metricValue(row[key], key)}</td>)}
      </tr>)}</tbody>
    </table>
  </div>;
};

const AdminAnalyticsPanel = ({ section, projects, users }) => {
  const [filter, setFilter] = useState(period);
  const [applied, setApplied] = useState(period);
  const [data, setData] = useState(null);
  const [personId, setPersonId] = useState("");
  const [appliedPersonId, setAppliedPersonId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isEngagement = section === "Work Contribution" || section === "User Activity & Engagement";
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError("");
    const params = { start: applied.start, end: applied.end,
      ...(applied.project_id ? { project_id: Number(applied.project_id) } : {}),
      ...(!isEngagement ? { timezone: applied.timezone } : {}),
      ...(appliedPersonId ? { [isEngagement ? "user_id" : section === "QA/QC Performance" ? "qa_user_id" : "inspector_id"]: Number(appliedPersonId) } : {}) };
    (isEngagement ? analyticsAPI.globalEngagement(params, ctrl.signal)
      : analyticsAPI.globalPerformance(params, ctrl.signal))
      .then(({ data: result }) => setData(result))
      .catch(e => { if (e?.code !== "ERR_CANCELED") setError(e?.response?.data?.detail || "Analytics request failed"); })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [applied, isEngagement, appliedPersonId, section]);

  const rows = isEngagement
    ? (data?.rows || []).filter(r => section === "Work Contribution" ? r.event_type !== "engagement_segment" : true)
    : (section === "Inspector Performance" ? data?.inspectors : data?.qa_qc);
  return <section className="space-y-4">
    <h3 className="text-xl font-semibold">{section}</h3>
    <form className="flex flex-wrap gap-3 text-sm" onSubmit={e => { e.preventDefault(); setApplied({ ...filter }); setAppliedPersonId(personId); }}>
      <label>Start <input className="border rounded p-2" type="date" value={filter.start} onChange={e => setFilter(v => ({ ...v, start: e.target.value }))} /></label>
      <label>End <input className="border rounded p-2" type="date" value={filter.end} onChange={e => setFilter(v => ({ ...v, end: e.target.value }))} /></label>
      <ProjectSelect projects={projects} value={filter.project_id} onChange={project_id => setFilter(v => ({ ...v, project_id }))} />
      {!isEngagement && <label>Timezone <input className="border rounded p-2 w-32" value={filter.timezone} onChange={e => setFilter(v => ({ ...v, timezone: e.target.value }))} /></label>}
      <UserSelect users={users} value={personId} onChange={setPersonId} label={section === "QA/QC Performance" ? "QA/QC Reviewer" : "Employee"} />
      <button className="bg-orange-600 text-white px-4 rounded" type="submit">Apply</button>
    </form>
    {isEngagement && <p className="text-xs text-slate-500">Daily aggregates are UTC. Visible/interaction-indicated time is not total working hours; overlapping tabs are deduplicated.</p>}
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {loading ? <p>Loading…</p> : <>
      {!isEngagement && data?.overview && <div className="flex gap-3 flex-wrap">{Object.entries(data.overview).map(([k,v]) =>
        <div key={k} className="rounded-lg border border-slate-200 bg-white p-3 text-sm"><div className="text-slate-500">{humanLabel(k)}</div><strong className="text-lg">{metricValue(v, k)}</strong></div>)}</div>}
      <AnalyticsTable rows={rows} projects={projects} users={users} />
    </>}
  </section>;
};

const ReportsPanel = ({ projects, users }) => {
  const [filter, setFilter] = useState(period);
  const [kind, setKind] = useState("consolidated");
  const [userId, setUserId] = useState("");
  const [inspectionType, setInspectionType] = useState("");
  const [category, setCategory] = useState("");
  const [authorized, setAuthorized] = useState({ allowed_recipients: [], groups: {} });
  const [recipientEmails, setRecipientEmails] = useState([]);
  const [reports, setReports] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const load = () => analyticsAPI.reports().then(r => setReports(r.data || []));
  useEffect(() => {
    load().catch(() => setMessage("Unable to load report history"));
    analyticsAPI.reportRecipients().then(({data}) => setAuthorized(data)).catch(() => {});
    const interval = setInterval(() => load().catch(() => {}), 15000);
    return () => clearInterval(interval);
  }, []);
  const generate = async (event) => {
    event.preventDefault(); setBusy(true); setMessage("");
    try {
      await analyticsAPI.createReport({ report_type: kind,
        period_start: filter.start, period_end: filter.end, reporting_timezone: filter.timezone,
        project_ids: filter.project_id ? [Number(filter.project_id)] : null,
        user_id: userId ? Number(userId) : null,
        inspection_type: inspectionType || null, user_category: category || null });
      await load(); setMessage("Report queued. Its status will update when generation finishes.");
    } catch (err) { setMessage(err?.response?.data?.detail || "Report generation failed"); }
    finally { setBusy(false); }
  };
  const download = async (report) => {
    setBusy(true); setMessage("");
    try {
      // Authorized endpoint issues a private S3 URL valid for 15 minutes;
      // file bytes never pass through App Runner.
      const { data } = await analyticsAPI.reportDownloadLink(report.id);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) { setMessage(err?.response?.data?.detail || "Download failed"); }
    finally { setBusy(false); }
  };
  const send = async (report) => {
    if (!recipientEmails.length || !window.confirm(`Send confidential report to ${recipientEmails.join(", ")}?`)) return;
    setBusy(true); setMessage("");
    try {
      await analyticsAPI.sendReport(report.id, recipientEmails);
      await load(); setMessage("Accepted by SES; actual mailbox delivery is not confirmed.");
    } catch (err) { setMessage(err?.response?.data?.detail || "Delivery request failed"); }
    finally { setBusy(false); }
  };
  const toggleEmail = (email) => setRecipientEmails(prev =>
    prev.includes(email) ? prev.filter(x => x !== email) : [...prev, email]);
  return <section className="space-y-4">
    <h3 className="text-xl font-semibold">Weekly Reports — Admin only</h3>
    <p className="text-xs text-slate-500">Files remain private. Report generation runs outside the HTTP request; a scheduled job recovers interrupted exports. Role categories reflect membership at export.</p>
    <form onSubmit={generate} className="flex flex-wrap gap-2 items-end text-sm">
      <label>Report <select className="border rounded p-2 block" value={kind} onChange={e => setKind(e.target.value)}>
        {[['inspector', 'Inspector Performance'], ['qa_qc', 'QA/QC Performance'], ['engagement', 'User Activity & Engagement'], ['consolidated', 'Consolidated Weekly Report']].map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select></label>
      {[['start', 'Start'], ['end', 'End']].map(([key, label]) => <label key={key}>{label}
        <input type="date" required className="border rounded p-2 block" value={filter[key]}
          onChange={e => setFilter(v => ({ ...v, [key]: e.target.value }))} /></label>)}
      <label>Timezone <input className="border rounded p-2 w-36 block" value={filter.timezone} onChange={e => setFilter(v => ({ ...v, timezone: e.target.value }))} /></label>
      <ProjectSelect projects={projects} value={filter.project_id} onChange={project_id => setFilter(v => ({ ...v, project_id }))} />
      <UserSelect users={users} value={userId} onChange={setUserId} label="Employee" />
      {(kind === "inspector" || kind === "consolidated") && <label>Inspection type <input className="border rounded p-2 w-32 block" value={inspectionType} onChange={e => setInspectionType(e.target.value)} /></label>}
      {(kind === "engagement" || kind === "consolidated") && <label>User category <select className="border rounded p-2 block" value={category} onChange={e => setCategory(e.target.value)}>
        {['', 'user_internal', 'user_external', 'foreman', 'general_foreman', 'project_engineer', 'supervisor', 'operations_manager', 'third_party'].map(x => <option key={x} value={x}>{x || 'All users'}</option>)}
      </select></label>}
      <button disabled={busy} className="bg-orange-600 disabled:opacity-50 text-white rounded p-2" type="submit">Generate</button>
    </form>
    <div className="border rounded p-3 space-y-2 text-sm">
      <h4 className="font-semibold">Approved Principal / HR recipients</h4>
      {Object.entries(authorized.groups || {}).map(([name, addresses]) =>
        <button key={name} type="button" className="mr-2 underline text-orange-700" onClick={() =>
          setRecipientEmails(old => [...new Set([...old, ...addresses])])}>{name} group</button>)}
      <div className="flex gap-3 flex-wrap">{(authorized.allowed_recipients || []).map(email =>
        <label key={email}><input type="checkbox" checked={recipientEmails.includes(email)} onChange={() => toggleEmail(email)} /> {email}</label>)}</div>
      <p className="text-xs text-slate-500">Email requires an explicit confirmation; only backend-approved addresses are shown.</p>
    </div>
    {message && <p role="status" className="text-sm">{message}</p>}
    <div className="overflow-x-auto"><table className="min-w-full text-sm"><thead><tr>
      {['Type', 'Period', 'Status', 'Generated', 'Delivery', 'Actions'].map(x => <th className="p-2 text-left" key={x}>{x}</th>)}</tr></thead>
      <tbody>{reports.map(report => <React.Fragment key={report.id}><tr className="border-b">
        <td className="p-2">{({inspector: 'Inspector Performance', qa_qc: 'QA/QC Performance', engagement: 'User Activity & Engagement', consolidated: 'Consolidated Weekly Report'})[report.report_type] || report.report_type}</td><td className="p-2">{report.period_start} – {report.period_end}</td>
        <td className="p-2">{report.status}</td><td className="p-2">{report.generated_at}</td>
        <td className="p-2">{report.delivery_status || 'Not sent'}</td>
        <td className="p-2 whitespace-nowrap">
          <button className="underline text-orange-700 mr-3" onClick={() => setPreviewId(id => id === report.id ? null : report.id)}>Preview</button>
          <button disabled={busy || report.status !== 'ready'} className="underline text-orange-700 mr-3 disabled:opacity-40" onClick={() => download(report)}>Download</button>
          <button disabled={busy || report.status !== 'ready' || !recipientEmails.length} className="underline text-orange-700 disabled:opacity-40" onClick={() => send(report)}>Send</button>
        </td></tr>
        {previewId === report.id && <tr><td colSpan={6} className="border-b p-3 bg-slate-50 text-xs">
          <strong>Report summary</strong>
          {report.error_message && <p className="text-red-700">{report.error_message}</p>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">{Object.entries(report.summary || {}).map(([key,value]) =>
            <div key={key} className="border rounded p-2"><span>{humanLabel(key)}</span><p className="font-semibold">{metricValue(value, key)}</p></div>)}</div>
        </td></tr>}
      </React.Fragment>)}</tbody></table></div>
  </section>;
};

const GlobalAnalyticsView = (props) => {
  const [section, setSection] = useState("Overview");
  const [options, setOptions] = useState({ projects: [], users: [] });
  const [optionsError, setOptionsError] = useState("");
  const requestedOptions = useRef(false);
  useEffect(() => {
    if (section === "Overview" || requestedOptions.current) return undefined;
    requestedOptions.current = true;
    // Reuse existing global project/user endpoints; request the dropdown data
    // once per Analytics mount rather than on every sub-tab switch.
    Promise.all([api.get('/projects'), api.get('/users')])
      .then(([projects, users]) => setOptions({ projects: projects.data || [], users: users.data || [] }))
      .catch(() => setOptionsError('Unable to load project and employee filters.'));
  }, [section]);
  return <div className="space-y-5">
    <h2 className="text-2xl font-bold">Analytics & Reports</h2>
    {optionsError && <p role="alert" className="text-sm text-amber-700">{optionsError}</p>}
    <nav className="flex gap-2 overflow-x-auto pb-2" aria-label="Analytics sections">
      {sections.map(x => <button key={x} onClick={() => setSection(x)}
        className={`whitespace-nowrap rounded px-3 py-2 text-sm ${section === x ? 'bg-orange-600 text-white' : 'bg-slate-100'}`}>{x}</button>)}
    </nav>
    {section === "Overview" ? <ExistingGlobalAnalyticsOverview {...props} /> :
      section === "Weekly Reports" ? <ReportsPanel {...options} /> :
      <AdminAnalyticsPanel key={section} section={section} {...options} />}
  </div>;
};
export default GlobalAnalyticsView;
